import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const formatToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const OrganizationStatsChart = ({ teacherStatsData }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const colors = [
    "#2B3674",
    "#D200A5",
    "#707EAE",
    "#FF8042",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
  ];


  const orgKeys = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];
    return [
      ...new Set(
        teacherStatsData.flatMap((item) =>
          Object.keys(item).filter((key) => key !== "date")
        )
      ),
    ];
  }, [teacherStatsData]);


  const processedData = useMemo(() => {
    if (!teacherStatsData || teacherStatsData.length === 0) return [];


    const dataWithAllKeys = teacherStatsData.map((item) => {
      const processedItem = { ...item };
      orgKeys.forEach((key) => {
        if (!(key in processedItem)) {
          processedItem[key] = 0;
        }
      });
      return processedItem;
    });


    const cumulativeData = [];
    const runningTotals = {};

    orgKeys.forEach((key) => {
      runningTotals[key] = 0;
    });

    dataWithAllKeys.forEach((item) => {
      const cumulativeItem = { ...item };

      orgKeys.forEach((key) => {
        runningTotals[key] += Number(item[key]) || 0;
        cumulativeItem[key] = runningTotals[key];
      });

      cumulativeData.push(cumulativeItem);
    });

    return cumulativeData;
  }, [teacherStatsData, orgKeys]);

  const maxVal = useMemo(() => {
    if (!processedData || processedData.length === 0) return 15;
    let max = 0;
    processedData.forEach((item) => {
      orgKeys.forEach((key) => {
        const val = Number(item[key]);
        if (!isNaN(val) && val > max) {
          max = val;
        }
      });
    });
    return max;
  }, [processedData, orgKeys]);

  const yAxisTicks = useMemo(() => {
    let limit = Math.max(15, maxVal);

    let step = 3;
    if (limit > 2000) step = 500;
    else if (limit > 1000) step = 200;
    else if (limit > 500) step = 100;
    else if (limit > 200) step = 50;
    else if (limit > 100) step = 20;
    else if (limit > 50) step = 10;
    else if (limit > 20) step = 5;

    limit = Math.ceil(limit / step) * step;
    const ticks = [];
    for (let i = 0; i <= limit; i += step) {
      ticks.push(i);
    }
    return ticks;
  }, [maxVal]);

  const requirements = useMemo(() => {
    const N = processedData?.length || 0;
    if (N <= 1) {
      return { shouldScroll: false, chartWidth: "100%", interval: 0 };
    }
    const requiredWidth = (N - 1) * 50 + 50;
    const containerWidth = width > 0 ? width : 500;
    const shouldScroll = containerWidth < requiredWidth;
    
    return {
      shouldScroll,
      chartWidth: shouldScroll ? `${requiredWidth}px` : "100%",
      interval: 0
    };
  }, [processedData?.length, width]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: requirements.shouldScroll ? "auto" : "hidden",
        overflowY: "hidden",
        boxSizing: "border-box",
        scrollbarWidth: requirements.shouldScroll ? "auto" : "none",
        msOverflowStyle: requirements.shouldScroll ? "auto" : "none",
      }}
      className="chart-scroll-container"
    >
      <div
        style={{
          width: requirements.chartWidth,
          minWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <ResponsiveContainer height={300}>
          <LineChart
            data={processedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatToDDMMYYYY}
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={requirements.interval}
            />
            <YAxis
              allowDecimals={false}
              ticks={yAxisTicks}
              domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
              interval={0}
            />
            <Tooltip
              labelFormatter={formatToDDMMYYYY}
              content={({ payload, label }) => {
                if (payload && payload.length > 0) {
                  const total = payload.reduce(
                    (sum, item) => sum + Number(item.value),
                    0
                  );

                  return (
                    <div
                      style={{
                        backgroundColor: "white",
                        padding: "10px",
                        border: "none",
                        borderRadius: "10px",
                        boxShadow: "0px 18px 40px rgba(112, 144, 176, 0.12)",
                      }}
                    >
                      <p style={{ fontWeight: "bold", color: "#2B3674", marginBottom: "5px" }}>{formatToDDMMYYYY(label)}</p>
                      {payload.map((item, index) => (
                        <div key={index} style={{ color: item.color, fontSize: "14px", display: "flex", justifyContent: "space-between", gap: "20px" }}>
                          <span>{item.name}:</span>
                          <span style={{ fontWeight: "bold" }}>{item.value}</span>
                        </div>
                      ))}
                      <div
                        style={{
                          borderTop: "1px solid #F4F7FE",
                          marginTop: "8px",
                          paddingTop: "8px",
                          fontWeight: "bold",
                          color: "#2B3674",
                          display: "flex",
                          justifyContent: "space-between"
                        }}
                      >
                        <span>Total:</span>
                        <span>{total}</span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              iconType="circle"
              wrapperStyle={{ paddingTop: "20px" }}
            />
            {orgKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colors[index % colors.length]}
                strokeWidth={3}
                dot={{ r: 4, fill: colors[index % colors.length] }}
                name={key}
                connectNulls={true}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <style>{`
        .chart-scroll-container {
          -webkit-overflow-scrolling: touch;
        }
        .chart-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .chart-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .chart-scroll-container::-webkit-scrollbar-thumb {
          background: #c3c3c3;
          border-radius: 4px;
        }
        .chart-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default OrganizationStatsChart;
